import React, { useState, useEffect } from 'react';
import { uploadSolution, downloadSolution, deleteSolution, validateFilenames, API_BASE_URL, getAuthHeaders } from '../api';
import { extractFilenames } from '../utils/filename-extraction';
import DownloadToast from './DownloadToast';
import PlaygroundModal from './PlaygroundModal';
import axios from 'axios';
import './SolutionUploadSection.css';

export type SolutionStatus = 'unknown' | 'missing' | 'uploaded';

interface SolutionUploadSectionProps {
  problemId: string;
  problemName: string;
  projectId: string;
  status: SolutionStatus;
  software?: string;
  problemPrompt?: string;
  onSolutionUploaded: () => void;
  isBadTask?: boolean;
  isReadOnly?: boolean;
}

// Software-specific accepted file types (matching task-platform/src/components/TaskSubmitModal.tsx)
const getAcceptedFileTypes = (software: string | undefined): string => {
  if (!software) return '.zip';

  const softwareLower = software.toLowerCase();

  switch (softwareLower) {
    case 'freecad':
      return '.FCStd,.fcstd';
    case 'blender':
      return '.blend';
    case 'musescore':
      return '.mscz';
    case 'kicad':
      return '.zip';
    case 'immich':
      return '.zip';
    case 'odoo':
      return '.zip';
    case 'erpnext':
      return '.zip';
    case 'audacity':
      return '.wav,.mp3,.flac,.ogg,.m4a,.aac,.wma,.aif,.aiff,.zip';
    case 'gimp':
      return '.png,.bmp,.tif,.tiff,.svg,.pdf,.webp,.psd,.gif,.zip';
    case 'libreoffice':
      return '.pdf,.docx,.odt,.ods,.odp,.xlsx,.pptx,.zip';
    case 'inkscape':
      return '.svg,.pdf,.png,.eps,.zip';
    case 'shotcut':
      return '.mlt,.mp4,.mov,.mkv,.webm,.zip';
    default:
      return '.zip';
  }
};

const getFileTypeDescription = (software: string | undefined): string => {
  if (!software) return 'ZIP archives';

  const softwareLower = software.toLowerCase();

  switch (softwareLower) {
    case 'freecad':
      return '.FCStd, .fcstd';
    case 'blender':
      return '.blend';
    case 'musescore':
      return '.mscz';
    case 'kicad':
      return '.zip';
    case 'audacity':
      return '.wav, .mp3, .flac, .ogg, .m4a, .aac, .wma, .aif, .aiff, .zip';
    case 'gimp':
      return '.png, .bmp, .tif, .tiff, .svg, .pdf, .webp, .psd, .gif, .zip';
    case 'libreoffice':
      return '.pdf, .docx, .odt, .ods, .odp, .xlsx, .pptx, .zip';
    case 'inkscape':
      return '.svg, .pdf, .png, .eps, .zip';
    case 'shotcut':
      return '.mlt, .mp4, .mov, .mkv, .webm, .zip';
    default:
      return '.zip';
  }
};

const SolutionUploadSection: React.FC<SolutionUploadSectionProps> = ({
  problemId,
  problemName,
  projectId,
  status,
  software,
  problemPrompt,
  onSolutionUploaded,
  isBadTask = false,
  isReadOnly = false,
}) => {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [downloadProgress, setDownloadProgress] = useState<number | null>(null);
  const [downloadFilename, setDownloadFilename] = useState<string>('');
  const [isValidatingFilename, setIsValidatingFilename] = useState(false);
  const [isPlaygroundModalOpen, setIsPlaygroundModalOpen] = useState(false);
  const [hasMattermostState, setHasMattermostState] = useState(false);
  const [checkingMattermostState, setCheckingMattermostState] = useState(false);

  const isUploaded = status === 'uploaded';
  const isWaived = isBadTask;

  // Multimedia software that can have Mattermost states
  const multimediaSoftware = ['gimp', 'audacity', 'inkscape', 'libreoffice', 'shotcut'];
  const isMultimediaSoftware = software && multimediaSoftware.includes(software.toLowerCase());

  useEffect(() => {
    if (isUploaded) {
      setSelectedFile(null);
      setUploading(false);
      setUploadError(null);
    }
  }, [isUploaded]);

  // Check if problem has Mattermost start state
  useEffect(() => {
    const checkMattermostState = async () => {
      if (!isMultimediaSoftware) {
        setHasMattermostState(false);
        return;
      }

      setCheckingMattermostState(true);
      try {
        const response = await axios.get(
          `${API_BASE_URL}/problems/${problemId}`,
          { headers: getAuthHeaders() }
        );

        const problem = response.data;
        const hasMattermost = problem?.spec?.start_state?.mattermost_state !== null &&
                              problem?.spec?.start_state?.mattermost_state !== undefined;
        setHasMattermostState(hasMattermost);
      } catch (error) {
        console.error('Failed to check Mattermost state:', error);
        setHasMattermostState(false);
      } finally {
        setCheckingMattermostState(false);
      }
    };

    checkMattermostState();
  }, [problemId, isMultimediaSoftware]);

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      console.log('[SolutionUploadSection] handleFileSelect - file.name:', file.name);
      console.log('[SolutionUploadSection] handleFileSelect - file.type:', file.type);
      setSelectedFile(file);
      setUploadError(null);
    }
  };

  const handleDragEnter = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();

    // Only set dragging to false if we're leaving the drop zone itself
    const target = e.currentTarget;
    const relatedTarget = e.relatedTarget as Node;

    if (!target.contains(relatedTarget)) {
      setIsDragging(false);
    }
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    if (isWaived) {
      return;
    }

    const files = e.dataTransfer?.files;
    if (files && files.length > 0) {
      const file = files[0];
      console.log('[SolutionUploadSection] handleDrop - file.name:', file.name);
      console.log('[SolutionUploadSection] handleDrop - file.type:', file.type);
      setSelectedFile(file);
      setUploadError(null);
    }
  };

  const validateSelectedFileWithAI = async (): Promise<{ isValid: boolean; errorMessage?: string }> => {
    if (!selectedFile) {
      return { isValid: false, errorMessage: 'Please select a solution file before uploading.' };
    }

    const promptText = problemPrompt ?? '';
    if (!promptText.trim()) {
      // Prompt not available, skip validation to avoid false negatives (fail open)
      return { isValid: true };
    }

    try {
      // Extract filenames from the selected file (including zip contents)
      console.log('[SolutionUploadSection] validateSelectedFileWithAI - selectedFile.name:', selectedFile.name);
      const solutionFilenames = await extractFilenames(selectedFile);
      console.log('[SolutionUploadSection] validateSelectedFileWithAI - extracted filenames:', solutionFilenames);

      // Validate filenames with AI
      const validationResult = await validateFilenames(promptText, solutionFilenames);
      console.log('[SolutionUploadSection] validateSelectedFileWithAI - validation result:', validationResult);

      // If validation failed and we have issues, return error
      if (!validationResult.is_valid && validationResult.issues.length > 0) {
        const issuesText = validationResult.issues.join('\n');
        const suggestionText = validationResult.suggestion ? `\n\nSuggestion: ${validationResult.suggestion}` : '';
        return { isValid: false, errorMessage: `${issuesText}${suggestionText}` };
      }

      // If there was an API error but we're failing open, log it but continue
      if (validationResult.error) {
        console.warn('Filename validation error (continuing anyway):', validationResult.error);
      }

      return { isValid: true };
    } catch (error) {
      console.error('Failed to validate filename:', error);
      // Fail open - continue with upload even if validation fails
      return { isValid: true };
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) {
      setUploadError('Please select a file first');
      return;
    }

    if (isReadOnly) {
      setUploadError('Tutorial mode: solution uploads are disabled.');
      return;
    }

    // Validate filename with AI
    setIsValidatingFilename(true);
    setUploadError(null);

    const validation = await validateSelectedFileWithAI();
    setIsValidatingFilename(false);

    if (!validation.isValid && validation.errorMessage) {
      setUploadError(validation.errorMessage);
      return;
    }

    // Proceed with upload
    try {
      setUploading(true);
      setUploadError(null);

      await uploadSolution(problemId, projectId, selectedFile);

      onSolutionUploaded();
      setSelectedFile(null);
    } catch (error: any) {
      console.error('Failed to upload solution:', error);
      setUploadError(error?.message || 'Failed to upload solution. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  const handleDownload = async () => {
    try {
      // Set initial state to show the toast
      setDownloadFilename('solution');
      setDownloadProgress(0);
      setUploadError(null);

      await downloadSolution(problemId, (progress) => {
        setDownloadProgress(progress);
      });

      // Keep the toast visible for a moment after completion
      setTimeout(() => {
        setDownloadProgress(null);
      }, 2000);
    } catch (error: any) {
      console.error('Failed to download solution:', error);
      setUploadError(error?.message || 'Failed to download solution. Please try again.');
      setDownloadProgress(null);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to delete this solution?')) {
      return;
    }

    try {
      await deleteSolution(problemId);
      // Refresh the parent - the parent should re-check the status
      onSolutionUploaded();
    } catch (error: any) {
      console.error('Failed to delete solution:', error);
      setUploadError(error?.message || 'Failed to delete solution. Please try again.');
    }
  };

  const acceptedTypes = getAcceptedFileTypes(software);
  const fileTypeDescription = getFileTypeDescription(software);
  const fileInputId = `solution-file-${problemId}`;

  const containerClassName = [
    'solution-upload-section',
    isUploaded ? 'has-solution' : '',
    isWaived ? 'waived' : '',
    isReadOnly && !isWaived ? 'readonly' : '',
    `status-${status}`,
  ]
    .filter(Boolean)
    .join(' ');

  const uploadAreaClassName = [
    'upload-area',
    isUploaded ? 'uploaded' : '',
    isWaived ? 'waived' : '',
    isReadOnly && !isWaived ? 'readonly' : '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div className={containerClassName}>
      <div className="upload-header">
        <h3>Upload Solution</h3>
      </div>
      <div className="upload-content-wrapper">
        <div
          className={`upload-instructions ${isWaived ? 'waived' : ''} ${
            isReadOnly && !isWaived ? 'readonly' : ''
          }`.trim()}
        >
          <ol>
            <li>
              <strong>Use the exact filename for the solution as specified in the prompt.</strong>
            </li>
            <li>Download the input file and solve the problem as stated.</li>
            <li>Upload either the solution file (if there is only one) or a ZIP archive containing all solution files (if there are multiple).</li>
          </ol>
        </div>


        <div className={uploadAreaClassName}>
          {isUploaded ? (
            <>
              <div className="uploaded-solution-header">
                <span className="success-badge">✓ Uploaded</span>
                <button
                  type="button"
                  className="delete-solution-btn"
                  onClick={handleDelete}
                  title="Delete solution"
                >
                  ✕
                </button>
              </div>
              <div
                className="download-solution-area"
                onClick={handleDownload}
                style={{ cursor: 'pointer' }}
              >
                <span className="download-icon">↓</span>
                <span className="download-text">Click to download solution</span>
              </div>
            </>
          ) : (
            <>
              <div
                className={`file-drop-zone ${
                  selectedFile ? 'has-file' : ''
                } ${isDragging ? 'dragging' : ''} ${isWaived ? 'waived' : ''} ${
                  isReadOnly && !isWaived ? 'readonly' : ''
                }`.trim()}
                onDragEnter={isWaived ? undefined : handleDragEnter}
                onDragLeave={isWaived ? undefined : handleDragLeave}
                onDragOver={isWaived ? undefined : handleDragOver}
                onDrop={isWaived ? undefined : handleDrop}
              >
                {!isWaived && (
                  <input
                    type="file"
                    id={fileInputId}
                    accept={acceptedTypes}
                    onChange={handleFileSelect}
                    disabled={uploading}
                  />
                )}
                <label
                  htmlFor={!isWaived ? fileInputId : undefined}
                  className={`file-drop-label ${isWaived ? 'waived' : ''} ${
                    isReadOnly && !isWaived ? 'readonly' : ''
                  }`.trim()}
                >
                  <span className="drop-title">
                    {isWaived
                      ? 'Solution upload waived for this problem'
                      : selectedFile
                      ? selectedFile.name
                      : 'Drag & drop your solution file'}
                  </span>
                  {!isWaived && (
                    <span className="drop-subtitle">
                      {selectedFile
                        ? 'Click to replace the selected file'
                        : 'or click to browse from your computer'}
                    </span>
                  )}
                  <span className="drop-formats">
                    {isWaived
                      ? 'Marked as Bad Task—solution upload not required'
                      : isReadOnly
                      ? 'Tutorial view – uploads are disabled in this walkthrough'
                      : `Accepted formats: ${fileTypeDescription}`}
                  </span>
                </label>
              </div>

              {!isWaived && (
                <button
                  type="button"
                  className="upload-button primary"
                  onClick={handleUpload}
                  disabled={!selectedFile || uploading || isValidatingFilename || isReadOnly}
                >
                  {isValidatingFilename ? (
                    <>
                      <span className="button-spinner"></span>
                      Validating filename...
                    </>
                  ) : uploading ? (
                    <>
                      <span className="button-spinner"></span>
                      Uploading...
                    </>
                  ) : (
                    <>
                      <span className="button-icon">↑</span>
                      Upload Solution
                    </>
                  )}
                </button>
              )}
            </>
          )}

          {isReadOnly && !isWaived && (
            <div className="tutorial-readonly-hint">
              Tutorial mode – uploads are disabled for this walkthrough.
            </div>
          )}

          {uploadError && (
            <div className="upload-error">
              <div className="upload-error-content">
                <span className="error-icon">⚠</span>
                <span style={{ whiteSpace: 'pre-wrap' }}>{uploadError}</span>
              </div>
            </div>
          )}
        </div>
      </div>

      {downloadProgress !== null && (
        <div style={{
          position: 'fixed',
          bottom: '20px',
          right: '20px',
          zIndex: 9999,
        }}>
          <DownloadToast
            filename={downloadFilename}
            progress={downloadProgress}
            onClose={() => setDownloadProgress(null)}
          />
        </div>
      )}

      <PlaygroundModal
        isOpen={isPlaygroundModalOpen}
        onClose={() => setIsPlaygroundModalOpen(false)}
        problemId={problemId}
        software={software || ''}
        problemPrompt={problemPrompt}
      />
    </div>
  );
};

export default SolutionUploadSection;
